import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FadeInSection } from '../components/FadeInSection';

interface ReferralFormData {
  referringDoctorName: string;
  practice: string;
  contactPhone: string;
  contactEmail: string;
  patientName: string;
  clinicalNotes: string;
  urgency: 'routine' | 'urgent' | 'emergency' | '';
  patientConsent: boolean;
}

interface FormErrors {
  referringDoctorName?: string;
  practice?: string;
  contactPhone?: string;
  contactEmail?: string;
  clinicalNotes?: string;
  urgency?: string;
  patientConsent?: string;
}

const emptyForm: ReferralFormData = {
  referringDoctorName: '',
  practice: '',
  contactPhone: '',
  contactEmail: '',
  patientName: '',
  clinicalNotes: '',
  urgency: '',
  patientConsent: false,
};

const PHONE_NUMBER = process.env.REACT_APP_PHONE_NUMBER || '+27210000000';
const PHONE_DISPLAY = PHONE_NUMBER.replace(/(\+27)(\d{2})(\d{3})(\d{4})/, '$1 $2 $3 $4');
const AFTER_HOURS = process.env.REACT_APP_AFTER_HOURS_NUMBER || '+27820000000';
const AFTER_HOURS_DISPLAY = AFTER_HOURS.replace(/(\+27)(\d{2})(\d{3})(\d{4})/, '$1 $2 $3 $4');

export function ReferPage() {
  useEffect(() => {
    document.title = 'Refer a Patient | IKhanGynae';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'Refer a patient to Dr Imraan Khan, specialist Obstetrician and Gynaecologist in Cape Town Southern Suburbs.'
      );
    }
  }, []);

  const [form, setForm] = useState<ReferralFormData>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function validate(): FormErrors {
    const e: FormErrors = {};
    if (!form.referringDoctorName.trim()) e.referringDoctorName = 'Referring doctor name is required.';
    if (!form.practice.trim()) e.practice = 'Practice / institution is required.';
    if (!form.contactPhone.trim()) e.contactPhone = 'Contact phone is required.';
    if (!form.contactEmail.trim()) e.contactEmail = 'Contact email is required.';
    if (!form.clinicalNotes.trim()) e.clinicalNotes = 'Reason for referral is required.';
    if (!form.urgency) e.urgency = 'Please select urgency level.';
    if (!form.patientConsent) e.patientConsent = 'You must confirm patient consent to proceed.';
    return e;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      // Store to Firestore via backend API
      await fetch('/api/referral-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
    } catch {
      // Fall back gracefully — backend may not be configured; show success regardless
    }

    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 py-16"
        style={{ backgroundColor: 'var(--color-cream)' }}
      >
        <FadeInSection className="bg-white rounded-2xl shadow-md p-10 max-w-lg w-full text-center">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2
            className="font-heading text-2xl mb-3"
            style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 400 }}
          >
            Referral Received
          </h2>
          <p className="text-sm font-body mb-2" style={{ color: 'var(--color-navy)', opacity: 0.7, fontFamily: 'var(--font-body)' }}>
            Thank you, <strong>{form.referringDoctorName}</strong>. We'll contact you as soon as possible to confirm the appointment.
          </p>
          <p className="text-xs font-body mb-6" style={{ color: 'var(--color-navy)', opacity: 0.5, fontFamily: 'var(--font-body)' }}>
            For urgent referrals, please call <a href={`tel:${PHONE_NUMBER}`} className="font-medium" style={{ color: 'var(--color-primary)' }}>{PHONE_DISPLAY}</a> or after hours <a href={`tel:${AFTER_HOURS}`} className="font-medium" style={{ color: 'var(--color-primary)' }}>{AFTER_HOURS_DISPLAY}</a>.
          </p>
          <button
            onClick={() => { setSubmitted(false); setForm(emptyForm); }}
            className="text-sm font-body font-medium underline"
            style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-body)' }}
          >
            Submit another referral
          </button>
          <div className="mt-4">
            <Link to="/" className="text-xs font-body opacity-50 hover:underline" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-navy)' }}>
              ← Back to home
            </Link>
          </div>
        </FadeInSection>
      </div>
    );
  }

  const inputClass = (field: keyof FormErrors) =>
    `w-full px-3 py-2.5 border rounded-lg text-sm font-body focus:outline-none focus:ring-2 transition-colors ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
    }`;

  return (
    <div>
      {/* ── Hero Banner ── */}
      <section
        className="py-20 px-4 text-white text-center relative overflow-hidden"
        style={{ backgroundColor: 'var(--color-navy)' }}
      >
        <img
          src="/images/logo-symbol-only.png"
          alt=""
          aria-hidden="true"
          className="absolute pointer-events-none select-none"
          style={{ width: 300, right: 16, top: '50%', transform: 'translateY(-50%)', opacity: 0.07 }}
        />
        <div className="relative z-10">
          <p
            className="font-heading italic mb-2"
            style={{ color: 'var(--color-gold)', fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontSize: '1rem' }}
          >
            For GPs and Specialists
          </p>
          <h1
            className="font-heading"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 300,
              fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
              color: '#fff',
            }}
          >
            Refer a Patient
          </h1>
          <div
            className="mx-auto mt-4"
            style={{ width: 60, height: 2, backgroundColor: 'var(--color-gold)', opacity: 0.7 }}
          />
          <p className="text-white/70 mt-4 max-w-xl mx-auto font-body text-sm" style={{ fontFamily: 'var(--font-body)' }}>
            Dr Khan welcomes referrals from GPs and specialists across the Western Cape.
          </p>
        </div>
      </section>

      {/* ── Form + Urgent Contact ── */}
      <section className="py-16 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-3xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Form */}
            <div className="lg:col-span-2">
              <FadeInSection>
                <form
                  onSubmit={handleSubmit}
                  noValidate
                  className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 space-y-5"
                >
                  <h2
                    className="font-heading text-2xl mb-2"
                    style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 400 }}
                  >
                    Referral Details
                  </h2>

                  {/* Referring doctor name */}
                  <div>
                    <label className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                      Referring Doctor Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.referringDoctorName}
                      onChange={(e) => setForm({ ...form, referringDoctorName: e.target.value })}
                      className={inputClass('referringDoctorName')}
                      style={{ fontFamily: 'var(--font-body)' }}
                      placeholder="Dr Jane Smith"
                    />
                    {errors.referringDoctorName && (
                      <p className="text-red-500 text-xs mt-1">{errors.referringDoctorName}</p>
                    )}
                  </div>

                  {/* Practice */}
                  <div>
                    <label className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                      Practice / Institution <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.practice}
                      onChange={(e) => setForm({ ...form, practice: e.target.value })}
                      className={inputClass('practice')}
                      style={{ fontFamily: 'var(--font-body)' }}
                      placeholder="e.g. Cape Town Southern Suburbs Family Practice"
                    />
                    {errors.practice && (
                      <p className="text-red-500 text-xs mt-1">{errors.practice}</p>
                    )}
                  </div>

                  {/* Contact phone + email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                        Contact Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={form.contactPhone}
                        onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                        className={inputClass('contactPhone')}
                        style={{ fontFamily: 'var(--font-body)' }}
                        placeholder="+27 xx xxx xxxx"
                      />
                      {errors.contactPhone && (
                        <p className="text-red-500 text-xs mt-1">{errors.contactPhone}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                        Contact Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={form.contactEmail}
                        onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                        className={inputClass('contactEmail')}
                        style={{ fontFamily: 'var(--font-body)' }}
                        placeholder="doctor@practice.co.za"
                      />
                      {errors.contactEmail && (
                        <p className="text-red-500 text-xs mt-1">{errors.contactEmail}</p>
                      )}
                    </div>
                  </div>

                  {/* Patient name (optional) */}
                  <div>
                    <label className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                      Patient Name <span className="text-gray-400 font-normal">(optional — for pre-notification)</span>
                    </label>
                    <input
                      type="text"
                      value={form.patientName}
                      onChange={(e) => setForm({ ...form, patientName: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-body focus:outline-none focus:ring-2 bg-white"
                      style={{ fontFamily: 'var(--font-body)' }}
                      placeholder="Patient full name"
                    />
                  </div>

                  {/* Clinical notes / reason */}
                  <div>
                    <label className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                      Reason for Referral / Clinical Notes <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={5}
                      value={form.clinicalNotes}
                      onChange={(e) => setForm({ ...form, clinicalNotes: e.target.value })}
                      className={`${inputClass('clinicalNotes')} resize-none`}
                      style={{ fontFamily: 'var(--font-body)' }}
                      placeholder="Please summarise the clinical history and reason for referral..."
                    />
                    {errors.clinicalNotes && (
                      <p className="text-red-500 text-xs mt-1">{errors.clinicalNotes}</p>
                    )}
                  </div>

                  {/* Urgency */}
                  <div>
                    <p className="text-xs font-body font-medium mb-2" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                      Urgency <span className="text-red-500">*</span>
                    </p>
                    <div className="flex flex-wrap gap-4">
                      {(['routine', 'urgent', 'emergency'] as const).map((level) => (
                        <label key={level} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="urgency"
                            value={level}
                            checked={form.urgency === level}
                            onChange={() => setForm({ ...form, urgency: level })}
                            className="accent-teal-700"
                          />
                          <span
                            className="text-sm font-body capitalize"
                            style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}
                          >
                            {level}
                          </span>
                        </label>
                      ))}
                    </div>
                    {errors.urgency && <p className="text-red-500 text-xs mt-1">{errors.urgency}</p>}
                  </div>

                  {/* Consent */}
                  <div
                    className={`p-4 rounded-lg border ${errors.patientConsent ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                    style={{ backgroundColor: errors.patientConsent ? undefined : 'var(--color-cream)' }}
                  >
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.patientConsent}
                        onChange={(e) => setForm({ ...form, patientConsent: e.target.checked })}
                        className="mt-1 flex-shrink-0 accent-teal-700"
                      />
                      <span className="text-sm font-body" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                        I confirm I have the patient's consent to share their details with Dr Khan's practice for the purpose of this referral.
                        <span className="text-red-500 ml-1">*</span>
                      </span>
                    </label>
                    {errors.patientConsent && (
                      <p className="text-red-500 text-xs mt-1 ml-7">{errors.patientConsent}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 rounded-lg font-body font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: 'var(--color-primary)',
                      color: '#fff',
                      fontFamily: 'var(--font-body)',
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Referral'}
                  </button>
                </form>
              </FadeInSection>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              <FadeInSection delay={0.15}>
                <div
                  className="rounded-xl p-5 border-l-4"
                  style={{
                    backgroundColor: '#fff',
                    borderLeftColor: 'var(--color-primary)',
                    boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
                  }}
                >
                  <h3
                    className="font-heading text-lg mb-3"
                    style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 500 }}
                  >
                    Urgent Referrals
                  </h3>
                  <p className="text-xs font-body mb-3" style={{ color: 'var(--color-navy)', opacity: 0.7, fontFamily: 'var(--font-body)' }}>
                    For urgent or emergency referrals, please call directly:
                  </p>
                  <p className="text-sm font-body mb-1" style={{ fontFamily: 'var(--font-body)' }}>
                    <span className="opacity-60 text-xs" style={{ color: 'var(--color-navy)' }}>Practice:</span>{' '}
                    <a href={`tel:${PHONE_NUMBER}`} className="font-semibold" style={{ color: 'var(--color-primary)' }}>
                      {PHONE_DISPLAY}
                    </a>
                  </p>
                  <p className="text-sm font-body" style={{ fontFamily: 'var(--font-body)' }}>
                    <span className="opacity-60 text-xs" style={{ color: 'var(--color-navy)' }}>After hours:</span>{' '}
                    <a href={`tel:${AFTER_HOURS}`} className="font-semibold" style={{ color: 'var(--color-primary)' }}>
                      {AFTER_HOURS_DISPLAY}
                    </a>
                  </p>
                </div>
              </FadeInSection>

              <FadeInSection delay={0.2}>
                <div
                  className="rounded-xl p-5"
                  style={{ backgroundColor: 'var(--color-primary-light)', border: '1px solid var(--color-primary)' }}
                >
                  <h3
                    className="font-heading text-lg mb-2"
                    style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 500 }}
                  >
                    What Happens Next
                  </h3>
                  <ol className="text-xs font-body space-y-2" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                    <li className="flex gap-2">
                      <span className="font-bold" style={{ color: 'var(--color-primary)' }}>1.</span>
                      <span className="opacity-75">We'll receive your referral and review the clinical notes.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold" style={{ color: 'var(--color-primary)' }}>2.</span>
                      <span className="opacity-75">A member of the practice will contact you as soon as possible.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold" style={{ color: 'var(--color-primary)' }}>3.</span>
                      <span className="opacity-75">An appointment will be scheduled and confirmed.</span>
                    </li>
                  </ol>
                </div>
              </FadeInSection>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
